/**
 * realizationsViewModel — `(reduxState, studentId) → props` des « Activités terminées » (patron A).
 *
 * Le legacy lit `user?.history?.activities` (`RealizationsTabScreen.js:68`), que le réducteur
 * remplit sur le succès de `getFinishedActivities` / `getStudentFinishedActivities` — page 1
 * remplace, les suivantes concatènent (`currentUser.js:1273-1300`). Le curseur de pagination,
 * lui, vit dans `useRealizationsData`.
 *
 * `studentId` choisit la tranche d'état comme il choisit le service côté données : avec un id
 * c'est l'appel « par élève », sans id l'appel « moi » (`RealizationsTabScreen.js:70-78`).
 */
import { createSelector } from "reselect"

import { toUIActivities } from "../../../adapters/parent/Activities/activityAdapter"
import { toUISubject } from "../../../adapters/parent/shared/schoolSubjectAdapter"
import { pickSearchSubjects } from "../../../ui/formatters/activitySearchFormatter"
import { type UIActivity, type UISubject } from "../../../ui/types"
import { selectDisplayedStudent } from "../../shared/displayedStudent"

export type RealizationsViewModel = {
    activities: UIActivity[]
    /** Toutes les activités qui passent la recherche, pages pas encore chargées comprises. */
    total: number
    /** Les matières que la barre de recherche propose : celles des activités faites, par nom. */
    searchSubjects: UISubject[]
    loading: boolean
    refreshing: boolean
    loadingMore: boolean
    error: unknown | null
}

/** Redux root is still untyped in this app — keep the VM boundary explicit. */
/* eslint-disable-next-line @typescript-eslint/no-explicit-any */
type ReduxState = any

export const realizationsViewModel = createSelector(
    [
        (state: ReduxState) => state?.data?.staticData?.school_subjects,
        (state: ReduxState) => selectDisplayedStudent(state)?.history?.activities,
        (state: ReduxState) => selectDisplayedStudent(state)?.history?.activities_meta,
        (state: ReduxState) => state?.api?.getStudentFinishedActivities,
        (state: ReduxState) => state?.api?.getFinishedActivities,
        (_state: ReduxState, studentId: string | number | null) => studentId,
    ],
    (
        /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
        subjects: any,
        /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
        rows: any,
        /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
        meta: any,
        /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
        byStudentReq: any,
        /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
        mineReq: any,
        studentId: string | number | null
    ): RealizationsViewModel => {
        const req = studentId != null ? byStudentReq : mineReq
        const requesting = !!req?.request
        const page = Number(req?.urlParams?.page ?? 1)
        const error = req?.error ?? null

        const activities = toUIActivities(rows, subjects ?? [])
        const hasRows = activities.length > 0

        return {
            activities,
            total: Number(meta?.total ?? activities.length),
            searchSubjects: pickSearchSubjects(meta?.school_subject_ids, subjects ?? []).map(toUISubject),
            loading: requesting && !hasRows,
            refreshing: requesting && hasRows && page <= 1,
            loadingMore: requesting && hasRows && page > 1,
            error: hasRows ? null : error,
        }
    }
)
